// client/src/components/Goals/GoalsPage.js
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Grid,
  Stack,
  LinearProgress,
  Alert,
  Skeleton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
} from '@mui/material';
import { Add as AddIcon, EmojiEvents as TrophyIcon } from '@mui/icons-material';
import GoalCard from './GoalCard';
import GoalForm from './GoalForm';
import { useAccount } from '../../contexts/AccountContext';
import api from '../../services/api';

const formatCurrency = (n) =>
  `€ ${Number(n || 0).toLocaleString('de-DE', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

/**
 * GoalsPage — full savings goals management page.
 * Fetches goals from /api/goals, shows summary bar + grid of GoalCards.
 * Optionally receives `categories` prop; falls back to empty array.
 */
const GoalsPage = ({ categories = [] }) => {
  const { accounts = [] } = useAccount();

  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Form dialog
  const [formOpen, setFormOpen] = useState(false);
  const [editingGoal, setEditingGoal] = useState(null);

  // Delete confirm dialog
  const [deleteTarget, setDeleteTarget] = useState(null);

  // Snackbar feedback
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'success' });
  const showSnack = (message, severity = 'success') =>
    setSnack({ open: true, message, severity });

  // ── Fetch goals ──
  const fetchGoals = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get('/goals');
      setGoals(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setError('Failed to load savings goals. Please try again.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchGoals();
  }, [fetchGoals]);

  // ── Summary stats ──
  const totalSaved = goals.reduce((s, g) => s + (g.currentAmount || 0), 0);
  const totalTarget = goals.reduce((s, g) => s + (g.targetAmount || 0), 0);
  const overallProgress =
    totalTarget > 0 ? Math.min(100, (totalSaved / totalTarget) * 100) : 0;
  const completedCount = goals.filter((g) => g.isCompleted).length;

  // ── CRUD handlers ──
  const handleSaveGoal = async (payload) => {
    try {
      const isEdit = Boolean(editingGoal && editingGoal._id);
      if (isEdit) {
        await api.put(`/goals/${editingGoal._id}`, payload);
      } else {
        await api.post('/goals', payload);
      }
      setFormOpen(false);
      setEditingGoal(null);
      await fetchGoals();
      showSnack(isEdit ? 'Goal updated!' : 'Goal created!');
    } catch (err) {
      showSnack(err.response?.data?.message || err.message || 'Failed to save goal', 'error');
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/goals/${deleteTarget._id}`);
      setDeleteTarget(null);
      await fetchGoals();
      showSnack('Goal deleted.');
    } catch (err) {
      showSnack(err.response?.data?.message || err.message || 'Failed to delete goal', 'error');
    }
  };

  const handleEdit = (goal) => {
    setEditingGoal(goal);
    setFormOpen(true);
  };

  const handleAddNew = () => {
    setEditingGoal(null);
    setFormOpen(true);
  };

  // ── Render ──
  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: 1100, mx: 'auto' }}>
      {/* Page header */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 3 }}
        flexWrap="wrap"
        gap={1}
      >
        <Box>
          <Typography variant="h5" fontWeight="bold">
            🎯 Savings Goals
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Track your financial targets and stay on course
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddNew}
        >
          Add Goal
        </Button>
      </Stack>

      {/* Summary bar */}
      {!loading && goals.length > 0 && (
        <Box
          sx={{
            p: 2,
            mb: 3,
            borderRadius: 2,
            bgcolor: 'background.paper',
            boxShadow: 1,
          }}
        >
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            justifyContent="space-between"
            alignItems={{ sm: 'center' }}
            sx={{ mb: 1.5 }}
          >
            <Box>
              <Typography variant="body2" color="text.secondary">
                Total saved
              </Typography>
              <Typography variant="h6" fontWeight="bold">
                {formatCurrency(totalSaved)}
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">
                Total target
              </Typography>
              <Typography variant="h6" fontWeight="bold">
                {formatCurrency(totalTarget)}
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">
                Overall progress
              </Typography>
              <Typography variant="h6" fontWeight="bold" color="success.main">
                {Math.round(overallProgress)}%
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">
                Completed
              </Typography>
              <Typography variant="h6" fontWeight="bold">
                {completedCount} / {goals.length}
              </Typography>
            </Box>
          </Stack>
          <LinearProgress
            variant="determinate"
            value={overallProgress}
            sx={{
              height: 8,
              borderRadius: 4,
              bgcolor: 'grey.200',
              '& .MuiLinearProgress-bar': { bgcolor: '#22c55e', borderRadius: 4 },
            }}
          />
        </Box>
      )}

      {/* Error */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Loading skeletons */}
      {loading && (
        <Grid container spacing={2}>
          {[1, 2, 3].map((k) => (
            <Grid item xs={12} sm={6} md={4} key={k}>
              <Skeleton variant="rounded" height={160} />
            </Grid>
          ))}
        </Grid>
      )}

      {/* Goals grid */}
      {!loading && goals.length > 0 && (
        <Grid container spacing={2}>
          {goals.map((goal) => (
            <Grid item xs={12} sm={6} md={4} key={goal._id}>
              <GoalCard
                goal={goal}
                onEdit={handleEdit}
                onDelete={(g) => setDeleteTarget(g)}
                formatCurrency={formatCurrency}
              />
            </Grid>
          ))}
        </Grid>
      )}

      {/* Empty state */}
      {!loading && goals.length === 0 && !error && (
        <Box
          sx={{
            textAlign: 'center',
            py: 8,
            px: 2,
            bgcolor: 'background.paper',
            borderRadius: 3,
            border: '2px dashed',
            borderColor: 'divider',
          }}
        >
          <TrophyIcon sx={{ fontSize: 64, color: '#22c55e', mb: 2 }} />
          <Typography variant="h6" fontWeight="bold" gutterBottom>
            No savings goals yet
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Create your first goal and start tracking your progress toward financial freedom.
          </Typography>
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleAddNew}>
            Create my first goal
          </Button>
        </Box>
      )}

      {/* Goal form dialog */}
      <GoalForm
        open={formOpen}
        onClose={() => {
          setFormOpen(false);
          setEditingGoal(null);
        }}
        onSave={handleSaveGoal}
        initialData={editingGoal}
        accounts={accounts}
        categories={categories}
      />

      {/* Delete confirm dialog */}
      <Dialog open={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)} maxWidth="xs">
        <DialogTitle>Delete goal?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete{' '}
            <strong>{deleteTarget?.name}</strong>? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDeleteConfirm}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          severity={snack.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default GoalsPage;
